import { Box, Flex, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { SIZES } from "@nens/ranalib";
import { useLocalStorage } from "@nens/react/hooks";
import { EMPTY_LAYER, EmptyLayers } from "@nens/react-map-gl/components";

import { BUILDING_LAYER_ID, RanaMapFullscreen } from "@/lib/rana-map";
import {
  ResizableSidebar,
  SIDEBAR_DEFAULT_WIDTH,
} from "@/lib/rana-map/components/rana-map-resizable-sidebar";
import { RanaTab, RanaTabs } from "@/lib/rana-tabs";

import { PublicationNoItemText } from "../../components/no-item-text";
import { PublicationNameEditable } from "../../components/publication-name-editable";
import { getAllLayers, getId, getOpenMap } from "../../store/selectors";
import { usePublicationStore } from "../../store/store";
import { Layer } from "../../types/publication";
import { PublicationActionBar } from "../action-bar";
import { DraftOrPublishedLabel } from "../detail/components/draft-or-published-label";
import {
  PublicationCommentThreadsOverview,
  PublicationMapContextMenu,
  useMapContextMenu,
  usePublicationMapComments,
} from "../map-comments";
import { PublicationLeftLayerList, PublicationLeftMapList } from "../map-layer-list";
import {
  AddPublicationLayerModal,
  PublicationMapRasterLayers,
  PublicationMapScenarioLayers,
  PublicationMapVectorLayers,
  PublicationPopup,
  usePublicationBounds,
  usePublicationMapModals,
  useVectorDescriptorSprites,
} from "../map-layers";
import { PublicationLegends } from "../map-legends";
import { usePublicationThumbnail } from "../map-thumbnail/hooks/use-publication-thumbnail";
import { PublicationThumbnail } from "../map-thumbnail/publication-thumbnail";
import { StudyAreaControl } from "../study-area";

import { PublicationMapBackButton } from "./components/back-button";
import { LayerControlHeader } from "./components/layer-control-header";
import { MapControlHeader } from "./components/map-control";
import { PUBLICATION_MAP_ID } from "./utils/map-id";

type PublicationMapProps = {
  closeMap: () => void;
};

const TOPBAR_HEIGHT = SIZES[16];

export function PublicationMap({ closeMap }: PublicationMapProps) {
  const intl = useIntl();
  const publicationId = usePublicationStore(getId);
  const { bounds, boundsLoading } = usePublicationBounds();
  const publicationCurrentOpenMap = usePublicationStore(getOpenMap);

  const {
    commentLayer,
    commentPopupConfig,
    addCommentToMapViaCoords,
    setIsAddingComment,
    commentHook,
  } = usePublicationMapComments(publicationId, publicationCurrentOpenMap.mapId);

  const { isAddingComment, selectedCommentThread } = commentHook;

  const { openModalType, closeModal, openAddLayerModal } = usePublicationMapModals();

  const {
    toggleContextMenu,
    showContextMenu,
    closeContextMenu,
    setShowContextMenu,
    contextMenuCoords,
  } = useMapContextMenu();

  const { sprites, isLoading: spritesLoading } = useVectorDescriptorSprites();

  const commentsNotAvailableText = intl.formatMessage({
    id: "map.cannot_comment",
    defaultMessage:
      "Cannot add comments to the draft version of a publication (currently under construction)",
  });

  const CONTROLS_RIGHT_TABS = {
    legends: {
      title: (
        <Text textTransform="uppercase" as="span">
          <FormattedMessage id="button.publication.legends" defaultMessage="Legends" />
        </Text>
      ),
      panel: (
        <Box mx="4">
          <PublicationLegends />
        </Box>
      ),
    },
    discuss: {
      title: (
        <Text textTransform="uppercase" as="span">
          <FormattedMessage id="button.publication.discuss" defaultMessage="Discuss" />
        </Text>
      ),
      panel: commentPopupConfig.canComment ? (
        <PublicationCommentThreadsOverview
          commentThreads={commentHook.commentThreads}
          selectedCommentThreadId={commentHook.selectedCommentThread?.id}
          setSelectedCommentThreadId={commentHook.setSelectedCommentThreadId}
          removeCommentThread={commentHook.removeCommentThread}
          editCommentMessage={commentHook.editCommentMessage}
          resolveComment={commentHook.resolveComment}
          showResolved={commentHook.showResolved}
          setShowResolved={commentHook.setShowResolved}
          toggleCommentReaction={commentHook.toggleCommentReaction}
        />
      ) : null,
      disabledTitle: commentsNotAvailableText,
      disabledPanel: <PublicationNoItemText>{commentsNotAvailableText}</PublicationNoItemText>,
    },
  } as const satisfies Record<string, RanaTab>;

  const allLayers = usePublicationStore(getAllLayers);
  const getBeforeId = (layer: Layer) =>
    EMPTY_LAYER + (99 - allLayers.findIndex(({ id }) => layer.id === id));

  const [[leftSidebarWidth, rightSidebarWidth], setSidebarsWidths] = useLocalStorage<
    [string, string]
  >("publication-sidebar-widths", [SIDEBAR_DEFAULT_WIDTH, SIDEBAR_DEFAULT_WIDTH]);

  const setLeftSidebarWidth = (width: string) => {
    setSidebarsWidths([width, rightSidebarWidth]);
  };

  const setRightSidebarWidth = (width: string) => {
    setSidebarsWidths([leftSidebarWidth, width]);
  };

  usePublicationThumbnail();

  return (
    <RanaMapFullscreen
      sprites={sprites}
      mapId={PUBLICATION_MAP_ID}
      bounds={bounds}
      mapProps={{
        onContextMenu: toggleContextMenu,
        onMove: closeContextMenu,
      }}
      deckGlProps={{ layers: [commentLayer] }}
      commentProps={commentPopupConfig}
      isLoading={boundsLoading || spritesLoading}
    >
      {openModalType === "add-layer" && <AddPublicationLayerModal onClose={closeModal} />}

      <EmptyLayers beforeId={BUILDING_LAYER_ID} />

      {/* Left side bar with menu items */}
      <ResizableSidebar
        side="left"
        topOffset={TOPBAR_HEIGHT}
        defaultWidth={leftSidebarWidth}
        onResizeStop={(_event, _direction, ref) => setLeftSidebarWidth(ref.offsetWidth + "px")}
      >
        <RanaMapFullscreen.ControlsLeft h="full" w="full">
          <MapControlHeader />
          <PublicationLeftMapList />
          <StudyAreaControl />
          <LayerControlHeader onClickAddIcon={openAddLayerModal} />
          <PublicationLeftLayerList />
        </RanaMapFullscreen.ControlsLeft>
      </ResizableSidebar>

      {/* Right side bar */}
      <ResizableSidebar
        side="right"
        topOffset={TOPBAR_HEIGHT}
        defaultWidth={rightSidebarWidth}
        onResizeStop={(_event, _direction, ref) => setRightSidebarWidth(ref.offsetWidth + "px")}
      >
        <RanaMapFullscreen.ControlsRight h="full" w="full">
          <RanaTabs
            tabs={CONTROLS_RIGHT_TABS}
            defaultTab={0}
            showBorder={false}
            tabProps={{ w: "full" }}
            tabPanelProps={{ marginTop: 2 }}
            onChange={(_index, key) => {
              if (key === "discuss") {
                setIsAddingComment(true);
              } else {
                setIsAddingComment(false);
              }
            }}
          />
        </RanaMapFullscreen.ControlsRight>
      </ResizableSidebar>

      {/* Top bar */}
      {/* Has to be below right sidebar because otherwise the menu will open below that */}
      <RanaMapFullscreen.ControlsTop
        h={TOPBAR_HEIGHT}
        w="full"
        borderBottomWidth="base"
        borderColor="brand.glacier"
        overflow=""
      >
        <Flex w="full" p={4} gap={2} justifyContent="space-between" alignItems="center">
          <Flex alignItems="center">
            <PublicationMapBackButton onClick={closeMap} />
            <PublicationThumbnail mapIsOnScreen ml={4} h={11} publicationId={publicationId} />
            <Box px={4}>
              <PublicationNameEditable />
            </Box>

            {/* FUTURE */}
            <DraftOrPublishedLabel />
          </Flex>
          <PublicationActionBar hideOpenViewer />
        </Flex>
      </RanaMapFullscreen.ControlsTop>

      <RanaMapFullscreen.MapScale right={`calc(${rightSidebarWidth} + ${SIZES[4]})`} />

      <PublicationMapRasterLayers getBeforeId={getBeforeId} />
      <PublicationMapVectorLayers getBeforeId={getBeforeId} />
      <PublicationMapScenarioLayers getBeforeId={getBeforeId} />

      {!isAddingComment && !selectedCommentThread && <PublicationPopup />}

      <PublicationMapContextMenu
        showContextMenu={showContextMenu}
        setShowContextMenu={setShowContextMenu}
        contextMenuCoords={contextMenuCoords}
        addCommentToMap={() => {
          if (contextMenuCoords.lng && contextMenuCoords.lat) {
            addCommentToMapViaCoords(contextMenuCoords.lng, contextMenuCoords.lat);
          }
        }}
      />
    </RanaMapFullscreen>
  );
}
